import type { GetStaticProps } from 'next'
import config from 'src/config'
import shopConfig from 'template/config'
import { post } from 'src/functions/fetch'
import {
  MenuPageBuilder,
  sanitizeMenuPageBuilder,
} from 'src/shared/dbTypes/menuPageBuilder'
import {
  LandingPageAboutPageSettings,
  LandingPageProductInquirySettings,
  sanitizeLandingPageShopSettings,
} from 'src/shared/dbTypes/landingPageSettings'
import {
  isFilterProductsResponse,
  type LandingPageImageAiGenerated,
} from 'src/shared/frontendTypes/productsFilter'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const sitePageBuilderCacheVersion = 4

const getStorageKey = () => `site-page-builder:v${sitePageBuilderCacheVersion}:${config.shop.shop}`

export interface SiteShopSettings {
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  aboutPage?: LandingPageAboutPageSettings
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

export const extractSitePageBuilder = (value: unknown): MenuPageBuilder | undefined => {
  if (isRecord(value)) {
    const topLevelBuilder = sanitizeMenuPageBuilder(value.sitePageBuilder)
    if (topLevelBuilder) return topLevelBuilder
  }

  if (!isFilterProductsResponse(value)) return undefined
  return sanitizeMenuPageBuilder(value.menu?.pageBuilder)
}

export const extractProductInquirySettings = (value: unknown): LandingPageProductInquirySettings | undefined => {
  if (!isRecord(value)) return undefined

  return sanitizeLandingPageShopSettings(value.shopSettings)?.productInquiry
}

export const extractAboutPageSettings = (value: unknown): LandingPageAboutPageSettings | undefined => {
  if (!isRecord(value)) return undefined

  return sanitizeLandingPageShopSettings(value.shopSettings)?.aboutPage
}

export const extractLandingPageImageAiGenerated = (
  value: unknown,
): LandingPageImageAiGenerated | undefined => {
  if (!isFilterProductsResponse(value, false)) return undefined
  return value.landingPageImageAiGenerated
}

export const extractSiteShopSettings = (value: unknown): SiteShopSettings => ({
  sitePageBuilder: extractSitePageBuilder(value),
  productInquiry: extractProductInquirySettings(value),
  aboutPage: extractAboutPageSettings(value),
  landingPageImageAiGenerated: extractLandingPageImageAiGenerated(value),
})

export const fetchSiteShopSettings = async ({
  local,
  signal,
  headers,
}: {
  local?: boolean
  signal?: AbortSignal
  headers?: Record<string, string>
} = {}): Promise<SiteShopSettings> => {
  const data = await post({
    url: 'products',
    body: {
      ...config.shop,
      homePage: true,
      builderOnly: true,
    },
    signal,
    local,
    headers,
  })

  return extractSiteShopSettings(data)
}

export const fetchSitePageBuilder = async ({
  local,
  signal,
  headers,
}: {
  local?: boolean
  signal?: AbortSignal
  headers?: Record<string, string>
} = {}): Promise<MenuPageBuilder | undefined> => {
  const { sitePageBuilder } = await fetchSiteShopSettings({ local, signal, headers })
  return sitePageBuilder
}

export const readCachedSitePageBuilder = (): MenuPageBuilder | undefined => {
  if (typeof window === 'undefined') return undefined

  try {
    const value = window.sessionStorage.getItem(getStorageKey())
    if (!value) return undefined

    return sanitizeMenuPageBuilder(JSON.parse(value))
  } catch {
    return undefined
  }
}

export const writeCachedSitePageBuilder = (pageBuilder?: MenuPageBuilder) => {
  if (typeof window === 'undefined') return

  try {
    if (!pageBuilder) {
      window.sessionStorage.removeItem(getStorageKey())
      return
    }

    window.sessionStorage.setItem(getStorageKey(), JSON.stringify(pageBuilder))
  } catch {
    return
  }
}

export interface SitePageBuilderProps {
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  aboutPage?: LandingPageAboutPageSettings
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

export const getSitePageBuilderStaticProps: GetStaticProps<SitePageBuilderProps> = async () => {
  try {
    const siteSettings = await fetchSiteShopSettings({ local: true })

    return {
      props: {
        ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
        ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
        ...(siteSettings.aboutPage ? { aboutPage: siteSettings.aboutPage } : {}),
        ...(siteSettings.landingPageImageAiGenerated
          ? { landingPageImageAiGenerated: siteSettings.landingPageImageAiGenerated }
          : {}),
      },
    }
  } catch {
    return { props: {} }
  }
}

/**
 * The about page is optional for every shop. Do not publish a generic page
 * when a shop has not explicitly supplied its own content.
 */
export const getAboutPageStaticProps: GetStaticProps<SitePageBuilderProps> = async () => {
  const siteSettings = await fetchSiteShopSettings({ local: true })

  const aboutPage = siteSettings.aboutPage ?? (shopConfig as {
    aboutPage?: LandingPageAboutPageSettings
  }).aboutPage
  const hasContent = aboutPage && [aboutPage.heroTitle, aboutPage.heroBody,
    aboutPage.introBodyHtml, aboutPage.storyBodyHtml].some(value => value?.trim())

  if (!hasContent || aboutPage?.enabled === false) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      aboutPage,
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(siteSettings.landingPageImageAiGenerated
        ? { landingPageImageAiGenerated: siteSettings.landingPageImageAiGenerated }
        : {}),
    },
    revalidate: 60,
  }
}
